import React, { useState, useRef, useCallback } from 'react';
import { Product, Connection, Interface, WorkflowNode, WorkflowCanvas, CONNECTION_TYPES } from '../../types/productIntegration';
import { 
  SAMPLE_PRODUCTS, 
  SAMPLE_CONNECTIONS, 
  getAllInterfaces, 
  getInterfaceById,
  addProduct,
  addInterface,
  updateProduct,
  updateInterface,
  deleteProduct,
  deleteInterface,
  addConnection
} from '../../data/integrationData';
import ProductForm from './ProductForm';
import InterfaceForm from './InterfaceForm';
import './WorkflowBuilder.css';
import { useDarkTheme } from '../../contexts/DarkThemeContext';

interface DraggedNode {
  id: string;
  offset: { x: number; y: number };
}

const WorkflowBuilder: React.FC = () => {
  const [canvas, setCanvas] = useState<WorkflowCanvas>({ nodes: [], connections: [] });
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isAddingConnection, setIsAddingConnection] = useState<boolean>(false);
  const [connectionSource, setConnectionSource] = useState<string | null>(null);
  const [showProductSelector, setShowProductSelector] = useState<boolean>(false);
  const [showInterfaceSelector, setShowInterfaceSelector] = useState<boolean>(false);
  const [pendingConnection, setPendingConnection] = useState<{sourceNodeId: string, targetNodeId: string} | null>(null);
  const [selectedConnectionType, setSelectedConnectionType] = useState<'single' | 'multiple'>('single');
  const [draggedNode, setDraggedNode] = useState<DraggedNode | null>(null);
  
  // CRUD modal states
  const [showProductForm, setShowProductForm] = useState<boolean>(false);
  const [showInterfaceForm, setShowInterfaceForm] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined);
  const [editingInterface, setEditingInterface] = useState<Interface | undefined>(undefined);
  
  // Welcome popup state
  const [showWelcomePopup, setShowWelcomePopup] = useState<boolean>(true);
  
  const canvasRef = useRef<HTMLDivElement>(null);

  // Dark theme support
  const { isDarkTheme } = useDarkTheme();
  const connectionColor = isDarkTheme ? '#888' : '#666';

  // Counter for unique node IDs to prevent duplicate keys
  const nodeCounterRef = useRef(0);

  // Helper function to get products connected by a specific interface
  const getProductsConnectedByInterface = useCallback((interfaceId: string): { sourceProduct: Product | undefined; targetProduct: Product | undefined } => {
    const connection = SAMPLE_CONNECTIONS.find(conn => conn.interface?.id === interfaceId);
    if (connection) {
      const sourceProduct = SAMPLE_PRODUCTS.find(p => p.id === connection.sourceProductId);
      const targetProduct = SAMPLE_PRODUCTS.find(p => p.id === connection.targetProductId);
      return { sourceProduct, targetProduct };
    }
    return { sourceProduct: undefined, targetProduct: undefined };
  }, []);

  // Add connected products to canvas when interface is selected
  const addInterfaceProductsToCanvas = useCallback((interfaceObj: Interface) => {
    console.log('🔍 addInterfaceProductsToCanvas called with interface:', interfaceObj.id, interfaceObj.name);
    
    const { sourceProduct, targetProduct } = getProductsConnectedByInterface(interfaceObj.id);
    
    console.log('🔍 Found products:', {
      sourceProduct: sourceProduct?.name,
      targetProduct: targetProduct?.name
    });
    
    if (sourceProduct && targetProduct) {
      console.log('✅ Both products found, creating canvas');
      
      // Clear existing canvas for clean interface view
      setCanvas({ nodes: [], connections: [] });
      
      // Reset node counter
      nodeCounterRef.current = 0;
      
      // Position nodes side by side with some separation
      const sourcePosition = { x: 200, y: 200 };
      const targetPosition = { x: 500, y: 200 };
      
      // Create nodes for both products
      const sourceNode: WorkflowNode = {
        id: `node-${Date.now()}-${++nodeCounterRef.current}`,
        productId: sourceProduct.id,
        position: sourcePosition,
        connections: []
      };
      
      const targetNode: WorkflowNode = {
        id: `node-${Date.now()}-${++nodeCounterRef.current}`,
        productId: targetProduct.id,
        position: targetPosition,
        connections: []
      };
      
      // Find the connection that uses this interface
      const connection = SAMPLE_CONNECTIONS.find(conn => conn.interface?.id === interfaceObj.id);
      
      if (connection) {
        console.log('✅ Connection found, creating workflow connection');
        
        // Create the workflow connection
        const workflowConnection: Connection = {
          ...connection,
          id: `conn-${Date.now()}`
        };
        
        // Update nodes with connection references
        sourceNode.connections = [workflowConnection.id];
        targetNode.connections = [workflowConnection.id];
        
        // Update canvas with nodes and connection
        setCanvas({
          nodes: [sourceNode, targetNode],
          connections: [workflowConnection]
        });
      } else {
        console.log('⚠️ No connection found, adding nodes only');
        
        // If no connection found, just add the nodes
        setCanvas({
          nodes: [sourceNode, targetNode],
          connections: []
        });
      }
      
      // Hide welcome popup when interface products are added
      setShowWelcomePopup(false);
    } else {
      console.log('❌ No products found for interface:', interfaceObj.id);
      console.log('Available connections:', SAMPLE_CONNECTIONS.map(c => ({
        id: c.id,
        interfaceId: c.interface?.id,
        interfaceName: c.interface?.name
      })));
      
      // Show an alert to the user that this interface isn't connected to any products
      alert(`Interface "${interfaceObj.name}" is not currently connected to any products. Please create a connection first or select a different interface.`);
    }
  }, [getProductsConnectedByInterface]);

  // Helper function to get a good position for new nodes
  const getNewNodePosition = useCallback(() => {
    if (canvas.nodes.length === 0) {
      // First node in center-left area
      return { x: 200, y: 200 };
    }
    
    // For subsequent nodes, position them to the right of existing nodes
    const rightmostNode = canvas.nodes.reduce((rightmost, node) => {
      return node.position.x > rightmost.position.x ? node : rightmost;
    }, canvas.nodes[0]);
    
    // Position new node to the right with some spacing
    const spacing = 350; // Increased space between nodes (node width 200px + larger gap)
    const newX = rightmostNode.position.x + spacing;
    
    // Keep same Y position as the first node for horizontal alignment
    const newY = 200;
    
    return {
      x: newX,
      y: newY
    };
  }, [canvas.nodes]);

  // Add product to canvas
  const addProductToCanvas = useCallback((product: Product, position?: { x: number; y: number }) => {
    const finalPosition = position || getNewNodePosition();
    
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}-${++nodeCounterRef.current}`, // Ensure unique keys
      productId: product.id,
      position: finalPosition,
      connections: []
    };

    setCanvas(prev => ({
      ...prev,
      nodes: [...prev.nodes, newNode]
    }));
    setShowProductSelector(false);
    setShowWelcomePopup(false); // Hide welcome popup when products are added
    
    // If we have a connection source (from single-product connection initiation), handle the connection
    if (connectionSource && canvas.nodes.length === 1) {
      handleAutoConnection(connectionSource, newNode.id, product);
    }
  }, [getNewNodePosition, connectionSource, canvas.nodes.length]);

  // Handle automatic connection after adding second product
  const handleAutoConnection = useCallback((sourceNodeId: string, targetNodeId: string, targetProduct: Product) => {
    const sourceNode = canvas.nodes.find(n => n.id === sourceNodeId);
    if (sourceNode) {
      const sourceProduct = SAMPLE_PRODUCTS.find(p => p.id === sourceNode.productId);
      
      // Check if this connection needs an interface
      const needsInterface = (sourceProduct?.type === 'internal' && targetProduct.type === 'external') ||
                           (sourceProduct?.type === 'external' && targetProduct.type === 'internal');
      
      if (needsInterface) {
        // Show interface selector for external-internal connections
        setPendingConnection({ sourceNodeId, targetNodeId });
        setShowInterfaceSelector(true);
        setConnectionSource(null);
      } else {
        // Create connection directly for internal-internal connections
        // Wait for next render cycle to ensure the new node is in the canvas
        setTimeout(() => {
          const newConnection: Connection = {
            id: `conn-${Date.now()}`,
            sourceProductId: sourceNode.productId,
            targetProductId: targetProduct.id,
            connectionType: selectedConnectionType,
            status: 'active',
            bidirectional: false,
            protocol: 'REST API',
            description: 'Integration connection'
          };

          setCanvas(prev => ({
            ...prev,
            connections: [...prev.connections, newConnection],
            nodes: prev.nodes.map(node => {
              if (node.id === sourceNodeId || node.id === targetNodeId) {
                return { ...node, connections: [...node.connections, newConnection.id] };
              }
              return node;
            })
          }));
        }, 0);
        setConnectionSource(null);
      }
    }
  }, [canvas.nodes, selectedConnectionType]);

  // Clear canvas
  const clearCanvas = useCallback(() => {
    setCanvas({ nodes: [], connections: [] });
    setSelectedNode(null);
    setIsAddingConnection(false);
    setConnectionSource(null);
    setPendingConnection(null);
    setShowProductSelector(false);
    setShowInterfaceSelector(false);
    nodeCounterRef.current = 0;
    // Show welcome popup again after clearing if no nodes exist
    setShowWelcomePopup(true);
  }, []);

  // Welcome popup handlers
  const handleMapExistingProducts = useCallback(() => {
    setShowWelcomePopup(false);
    setShowProductSelector(true);
  }, []);

  const handleCreateNewProduct = useCallback(() => {
    setShowWelcomePopup(false);
    handleCreateProduct();
  }, []);

  const handleCreateNewInterface = useCallback(() => {
    setShowWelcomePopup(false);
    handleCreateInterface();
  }, []);

  const handleCloseWelcomePopup = useCallback(() => {
    setShowWelcomePopup(false);
  }, []);

  // CRUD Handlers for Products
  const handleCreateProduct = useCallback(() => {
    setEditingProduct(undefined);
    setShowProductForm(true);
  }, []);

  const handleEditProduct = useCallback((product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  }, []);

  const handleSaveProduct = useCallback((productData: Omit<Product, 'id'> | Product) => {
    if ('id' in productData) {
      // Updating existing product
      const updated = updateProduct(productData.id, productData);
      if (updated) {
        console.log('Product updated:', updated);
        // Update any canvas nodes that reference this product
        setCanvas(prev => ({
          ...prev,
          nodes: prev.nodes.map(node => 
            node.productId === productData.id ? { ...node } : node
          )
        }));
      }
    } else {
      // Creating new product
      const newProduct = addProduct(productData);
      console.log('Product created:', newProduct);
    }
    setShowProductForm(false);
    setEditingProduct(undefined);
  }, []);

  const handleDeleteProduct = useCallback((productId: string) => {
    if (window.confirm('Are you sure you want to delete this product? This will also remove any associated connections.')) {
      const success = deleteProduct(productId);
      if (success) {
        console.log('Product deleted:', productId);
        // Remove any canvas nodes that reference this product
        setCanvas(prev => ({
          nodes: prev.nodes.filter(node => node.productId !== productId),
          connections: prev.connections.filter(conn => 
            conn.sourceProductId !== productId && conn.targetProductId !== productId
          )
        }));
      }
    }
  }, []);

  // CRUD Handlers for Interfaces
  const handleCreateInterface = useCallback(() => {
    setEditingInterface(undefined);
    setShowInterfaceForm(true);
  }, []);

  const handleEditInterface = useCallback((interfaceData: Interface) => {
    setEditingInterface(interfaceData);
    setShowInterfaceForm(true);
  }, []);

  const handleSaveInterface = useCallback((interfaceData: Omit<Interface, 'id'> | Interface) => {
    if ('id' in interfaceData) {
      // Updating existing interface
      const updated = updateInterface(interfaceData.id, interfaceData);
      if (updated) {
        console.log('Interface updated:', updated);
        // Update any canvas connections that reference this interface
        setCanvas(prev => ({
          ...prev,
          connections: prev.connections.map(conn => 
            conn.interface?.id === interfaceData.id 
              ? { ...conn, interface: updated }
              : conn
          )
        }));
      }
    } else {
      // Creating new interface
      const newInterface = addInterface(interfaceData);
      console.log('Interface created:', newInterface);
    }
    setShowInterfaceForm(false);
    setEditingInterface(undefined);
  }, []);

  const handleDeleteInterface = useCallback((interfaceId: string) => {
    if (window.confirm('Are you sure you want to delete this interface? This will remove the interface from any associated connections.')) {
      const success = deleteInterface(interfaceId);
      if (success) {
        console.log('Interface deleted:', interfaceId);
        // Update canvas connections to remove the interface reference
        setCanvas(prev => ({
          ...prev,
          connections: prev.connections.map(conn => 
            conn.interface?.id === interfaceId 
              ? { ...conn, interface: undefined }
              : conn
          )
        }));
      }
    }
  }, []);

  // Handle canvas click for adding products
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (showProductSelector) {
      // Just close the product selector if clicking on canvas while it's open
      setShowProductSelector(false);
      // If we were in connection mode, clear it
      if (connectionSource) {
        setConnectionSource(null);
        setIsAddingConnection(false);
      }
    }
    if (showInterfaceSelector && !pendingConnection) {
      // Close interface selector if clicking on canvas (but not during connection creation)
      setShowInterfaceSelector(false);
    }
    if (isAddingConnection && !showProductSelector && !showInterfaceSelector) {
      // If we're in connection mode and click on empty canvas, cancel connection
      setIsAddingConnection(false);
      setConnectionSource(null);
    }
  }, [showProductSelector, showInterfaceSelector, pendingConnection, isAddingConnection, connectionSource]);

  // Handle drag and drop for interfaces
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    try {
      const data = event.dataTransfer.getData('application/json');
      if (data) {
        const droppedItem = JSON.parse(data);
        
        // Check if it's an interface being dropped
        if (droppedItem.type === 'interface' && droppedItem.interface) {
          addInterfaceProductsToCanvas(droppedItem.interface);
        }
        // Could also handle product drops here if needed
        else if (droppedItem.type === 'product' && droppedItem.product) {
          const rect = canvasRef.current?.getBoundingClientRect();
          if (rect) {
            const position = {
              x: event.clientX - rect.left,
              y: event.clientY - rect.top
            };
            addProductToCanvas(droppedItem.product, position);
          }
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [addInterfaceProductsToCanvas, addProductToCanvas]);

  // Start connection creation - enhanced logic
  const startConnection = useCallback((nodeId: string) => {
    // If there's only one product on canvas, open product selector to add another
    if (canvas.nodes.length === 1) {
      setShowProductSelector(true);
      setConnectionSource(nodeId); // Remember which node initiated the connection
      return;
    }
    
    // If there are 2+ products, enter connection mode
    setConnectionSource(nodeId);
    setIsAddingConnection(true);
  }, [canvas.nodes.length]);

  // Complete connection creation
  const completeConnection = useCallback((targetNodeId: string) => {
    if (connectionSource && connectionSource !== targetNodeId) {
      const sourceNode = canvas.nodes.find(n => n.id === connectionSource);
      const targetNode = canvas.nodes.find(n => n.id === targetNodeId);
      
      if (sourceNode && targetNode) {
        const sourceProduct = SAMPLE_PRODUCTS.find(p => p.id === sourceNode.productId);
        const targetProduct = SAMPLE_PRODUCTS.find(p => p.id === targetNode.productId);
        
        // Check if this connection is between internal and external products
        const needsInterface = (sourceProduct?.type === 'internal' && targetProduct?.type === 'external') ||
                             (sourceProduct?.type === 'external' && targetProduct?.type === 'internal');
        
        if (needsInterface) {
          // Show interface selector for external-internal connections
          setPendingConnection({ sourceNodeId: connectionSource, targetNodeId });
          setShowInterfaceSelector(true);
          setIsAddingConnection(false);
          setConnectionSource(null);
        } else {
          // Create connection directly for internal-internal connections
          createConnection(connectionSource, targetNodeId);
        }
      }
    }
  }, [connectionSource, selectedConnectionType, canvas.nodes]);

  // Create connection with optional interface
  const createConnection = useCallback((sourceNodeId: string, targetNodeId: string, selectedInterface?: Interface) => {
    const sourceNode = canvas.nodes.find(n => n.id === sourceNodeId);
    const targetNode = canvas.nodes.find(n => n.id === targetNodeId);
    
    if (sourceNode && targetNode) {
      const newConnection: Connection = {
        id: `conn-${Date.now()}`,
        sourceProductId: sourceNode.productId,
        targetProductId: targetNode.productId,
        connectionType: selectedConnectionType,
        status: 'active',
        bidirectional: false,
        protocol: selectedInterface?.protocol || 'REST API',
        description: selectedInterface?.description || 'Integration connection',
        ...(selectedInterface && { interface: selectedInterface })
      };

      setCanvas(prev => ({
        ...prev,
        connections: [...prev.connections, newConnection],
        nodes: prev.nodes.map(node => {
          if (node.id === sourceNodeId) {
            return { ...node, connections: [...node.connections, newConnection.id] };
          }
          if (node.id === targetNodeId) {
            return { ...node, connections: [...node.connections, newConnection.id] };
          }
          return node;
        })
      }));
    }
    
    setIsAddingConnection(false);
    setConnectionSource(null);
    setPendingConnection(null);
    setShowInterfaceSelector(false);
  }, [selectedConnectionType, canvas.nodes]);

  // Remove node from canvas
  const removeNodeFromCanvas = useCallback((nodeId: string) => {
    setCanvas(prev => {
      // Find the node to remove
      const nodeToRemove = prev.nodes.find(n => n.id === nodeId);
      if (!nodeToRemove) return prev;
      
      // Remove all connections that involve this node
      const connectionsToRemove = prev.connections.filter(conn => 
        conn.sourceProductId === nodeToRemove.productId || 
        conn.targetProductId === nodeToRemove.productId
      );
      
      // Remove the node and its connections
      return {
        nodes: prev.nodes.filter(n => n.id !== nodeId),
        connections: prev.connections.filter(conn => 
          conn.sourceProductId !== nodeToRemove.productId && 
          conn.targetProductId !== nodeToRemove.productId
        )
      };
    });
    
    // Clear selection if the removed node was selected
    if (selectedNode === nodeId) {
      setSelectedNode(null);
    }
  }, [selectedNode]);

  // Handle node drag
  const handleMouseDown = useCallback((event: React.MouseEvent, nodeId: string) => {
    const node = canvas.nodes.find(n => n.id === nodeId);
    if (node && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDraggedNode({
        id: nodeId,
        offset: {
          x: event.clientX - rect.left - node.position.x,
          y: event.clientY - rect.top - node.position.y
        }
      });
    }
  }, [canvas.nodes]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (draggedNode && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const newPosition = {
        x: event.clientX - rect.left - draggedNode.offset.x,
        y: event.clientY - rect.top - draggedNode.offset.y
      };

      setCanvas(prev => ({
        ...prev,
        nodes: prev.nodes.map(node =>
          node.id === draggedNode.id
            ? { ...node, position: newPosition }
            : node
        )
      }));
    }
  }, [draggedNode]);

  const handleMouseUp = useCallback(() => {
    setDraggedNode(null);
  }, []);

  // Render connection lines (SVG)
  const renderConnections = () => {
    return canvas.connections.map(connection => {
      const sourceNode = canvas.nodes.find(n => n.productId === connection.sourceProductId);
      const targetNode = canvas.nodes.find(n => n.productId === connection.targetProductId);
      
      if (!sourceNode || !targetNode) return null;

      // Connect from the right edge connector to the left edge of target node
      const x1 = sourceNode.position.x + 200; // Right edge of source node
      const y1 = sourceNode.position.y + 60;  // Middle of source node (reduced height)
      const x2 = targetNode.position.x;       // Left edge of target node
      const y2 = targetNode.position.y + 60;  // Middle of target node

      return (
        <line
          key={connection.id}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={connectionColor}
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
        />
      );
    });
  };

  // Get product by ID
  const getProduct = (productId: string) => SAMPLE_PRODUCTS.find(p => p.id === productId);

  return (
    <div className="workflow-builder">
      <div className="workflow-header">
        <h1>Integration Mapper & Builder</h1>
        <div className="workflow-controls">
          <button
            className="create-product-btn"
            onClick={handleCreateProduct}
            title="Create a new product"
          >
            <span className="plus-icon">+</span>
            Create Product
          </button>
          <button
            className="create-interface-btn"
            onClick={handleCreateInterface}
            title="Create a new interface"
          >
            <span className="plus-icon">+</span>
            Create Interface
          </button>
          <button
            className={`add-product-btn ${showProductSelector ? 'active' : ''}`}
            onClick={() => setShowProductSelector(!showProductSelector)}
          >
            <span className="plus-icon">+</span>
            Select Product
          </button>
          <button
            className={`add-product-btn ${showInterfaceSelector && !pendingConnection ? 'active' : ''}`}
            onClick={() => {
              setPendingConnection(null); // Clear any pending connection
              setShowInterfaceSelector(!showInterfaceSelector);
            }}
          >
            <span className="plus-icon">⚡</span>
            Select Interface
          </button>
          <select
            value={selectedConnectionType}
            onChange={(e) => setSelectedConnectionType(e.target.value as 'single' | 'multiple')}
          >
            <option value="single">Single Connection</option>
            <option value="multiple">Multiple Connection</option>
          </select>
          <button
            className="clear-button"
            onClick={clearCanvas}
            disabled={canvas.nodes.length === 0}
            title="Clear all products and connections from canvas"
          >
            Clear
          </button>
        </div>
      </div>

      <div 
        className="workflow-canvas"
        ref={canvasRef}
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* SVG for connection lines */}
        <svg className="connections-layer">
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill={connectionColor}
              />
            </marker>
          </defs>
          {renderConnections()}
        </svg>

        {/* Connection Status Overlay */}
        {isAddingConnection && connectionSource && canvas.nodes.length > 1 && (
          <div className="connection-status-overlay">
            <span className="connection-status-icon">🔗</span>
            <span>Click on another product to create connection</span>
          </div>
        )}

        {/* Workflow nodes */}
        {canvas.nodes.map(node => {
          const product = getProduct(node.productId);
          if (!product) return null;

          return (
            <div
              key={node.id}
              className={`workflow-node ${product.type} ${selectedNode === node.id ? 'selected' : ''}`}
              style={{
                position: 'absolute',
                left: node.position.x,
                top: node.position.y,
                cursor: draggedNode?.id === node.id ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleMouseDown(e, node.id)}
              onClick={(e) => {
                e.stopPropagation();
                if (isAddingConnection) {
                  completeConnection(node.id);
                } else {
                  setSelectedNode(node.id);
                }
              }}
            >
              <div className="node-header">
                <span className="node-type-indicator">{product.type === 'internal' ? 'MSI' : 'EXT'}</span>
                <h3>{product.name}</h3>
                
                {/* Action buttons - appear on hover */}
                <div className="node-actions">
                  <button
                    className="node-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProduct(product);
                    }}
                    title="Edit product"
                  >
                    ✏️
                  </button>
                  <button
                    className="node-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNodeFromCanvas(node.id);
                    }}
                    title="Remove from canvas"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="node-body">
                <p className="node-category">{product.category}</p>
                <p className="node-team">{product.team}</p>
              </div>
              
              {/* Connection connector on the right edge */}
              <div 
                className={`node-connector ${
                  isAddingConnection && connectionSource === node.id 
                    ? 'waiting-for-target' 
                    : isAddingConnection && connectionSource === node.id 
                    ? 'active' 
                    : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isAddingConnection && connectionSource === node.id) {
                    // If we're already in connection mode from this node, show target selection
                    return;
                  } else if (isAddingConnection) {
                    // If we're in connection mode from another node, complete the connection
                    completeConnection(node.id);
                  } else {
                    // Start new connection from this node
                    startConnection(node.id);
                  }
                }}
                title={
                  isAddingConnection && connectionSource === node.id 
                    ? 'Select target product to connect' 
                    : isAddingConnection 
                    ? 'Click to complete connection' 
                    : canvas.nodes.length === 1 
                    ? 'Add product and connect' 
                    : 'Create connection'
                }
              >
                <span className="connector-icon">+</span>
              </div>
              
              {/* Connection line from node to connector */}
              <div className="connection-line"></div>
            </div>
          );
        })}

        {/* Product selector overlay */}
        {showProductSelector && (
          <div className="product-selector-overlay">
            <div className="product-selector">
              <h3>
                {connectionSource && canvas.nodes.length === 1 
                  ? 'Select Product to Connect' 
                  : 'Select Product to Add to Canvas'
                }
              </h3>
              {connectionSource && canvas.nodes.length === 1 && (
                <p className="connection-context">
                  Creating connection from: <strong>{getProduct(canvas.nodes.find(n => n.id === connectionSource)?.productId || '')?.name}</strong>
                </p>
              )}
              <div className="product-categories">
                <div className="internal-products">
                  <h4>Internal Products (MSI)</h4>
                  {SAMPLE_PRODUCTS.filter(p => p.type === 'internal' && !canvas.nodes.some(n => n.productId === p.id))
                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                    .map(product => (
                      <button
                        key={product.id}
                        className="product-option internal"
                        onClick={() => addProductToCanvas(product)}
                      >
                        <span className="product-name">{product.name}</span>
                        <span className="product-category">{product.category}</span>
                      </button>
                    ))}
                </div>
                <div className="external-products">
                  <h4>External Products</h4>
                  {SAMPLE_PRODUCTS.filter(p => p.type === 'external' && !canvas.nodes.some(n => n.productId === p.id))
                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                    .map(product => (
                      <button
                        key={product.id}
                        className="product-option external"
                        onClick={() => addProductToCanvas(product)}
                      >
                        <span className="product-name">{product.name}</span>
                        <span className="product-category">{product.category}</span>
                      </button>
                    ))}
                </div>
              </div>
              <button
                className="close-selector"
                onClick={() => {
                  setShowProductSelector(false);
                  // If we were in connection mode, clear the connection source
                  if (connectionSource) {
                    setConnectionSource(null);
                    setIsAddingConnection(false);
                  }
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Interface selector overlay */}
        {showInterfaceSelector && (
          <div className="interface-selector-overlay">
            <div className="interface-selector">
              <h3>
                {pendingConnection 
                  ? 'Select Interface for Integration' 
                  : 'Select Interface to View Connected Products'
                }
              </h3>
              <p className="interface-info">
                {pendingConnection 
                  ? 'This connection requires an interface between internal and external systems.'
                  : 'Choose an interface to automatically add the products it connects to the canvas.'
                }
              </p>
              <div className="interface-options">
                {getAllInterfaces()
                  .filter(iface => iface.status === 'active' || iface.status === 'planned')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(iface => (
                    <button
                      key={iface.id}
                      className={`interface-option ${iface.status}`}
                      onClick={() => {
                        if (pendingConnection) {
                          // Creating connection between selected nodes
                          createConnection(
                            pendingConnection.sourceNodeId, 
                            pendingConnection.targetNodeId, 
                            iface
                          );
                        } else {
                          // Standalone interface selection - show connected products
                          addInterfaceProductsToCanvas(iface);
                          setShowInterfaceSelector(false);
                        }
                      }}
                    >
                      <div className="interface-header">
                        <span className="interface-name">{iface.name}</span>
                        <span className={`interface-status ${iface.status}`}>{iface.status}</span>
                      </div>
                      <div className="interface-details">
                        <span className="interface-protocol">{iface.protocol}</span>
                        <span className="interface-direction">{iface.direction}</span>
                      </div>
                      <div className="interface-description">{iface.description}</div>
                    </button>
                  ))}
              </div>
              <div className="interface-actions">
                {pendingConnection && (
                  <button
                    className="create-without-interface"
                    onClick={() => createConnection(
                      pendingConnection.sourceNodeId, 
                      pendingConnection.targetNodeId
                    )}
                  >
                    Create Connection Without Interface
                  </button>
                )}
                <button
                  className="close-selector"
                  onClick={() => {
                    setShowInterfaceSelector(false);
                    setPendingConnection(null);
                    setIsAddingConnection(false);
                    setConnectionSource(null);
                  }}
                >
                  {pendingConnection ? 'Cancel' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Welcome Popup */}
      {showWelcomePopup && canvas.nodes.length === 0 && (
        <div className="modal-overlay welcome-popup-overlay">
          <div className="modal-content welcome-popup">
            <div className="modal-header">
              <h3>🚀 Welcome to Integration Mapper & Builder</h3>
              <button className="close-modal" onClick={handleCloseWelcomePopup}>×</button>
            </div>
            
            <div className="welcome-content">
              <p className="welcome-description">
                Get started by choosing one of the options below to begin building your integration workflows.
              </p>
              
              <div className="welcome-options">
                <div className="welcome-option">
                  <div className="option-icon">🔗</div>
                  <h4>Map Existing Products</h4>
                  <p>Connect and visualize relationships between existing products. Click a product's + connector to easily link with others.</p>
                  <button 
                    className="welcome-option-btn primary-option"
                    onClick={handleMapExistingProducts}
                  >
                    Select Products to Map
                  </button>
                </div>
                
                <div className="welcome-option">
                  <div className="option-icon">📦</div>
                  <h4>Create New Product</h4>
                  <p>Add a new product to your integration catalog and define its properties.</p>
                  <button 
                    className="welcome-option-btn secondary-option"
                    onClick={handleCreateNewProduct}
                  >
                    Create Product
                  </button>
                </div>
                
                <div className="welcome-option">
                  <div className="option-icon">⚡</div>
                  <h4>Create New Interface</h4>
                  <p>Define a new interface specification for connecting products.</p>
                  <button 
                    className="welcome-option-btn secondary-option"
                    onClick={handleCreateNewInterface}
                  >
                    Create Interface
                  </button>
                </div>
              </div>
              
              <div className="welcome-footer">
                <p>You can access these options anytime from the toolbar above.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workflow summary */}
      <div className="workflow-summary">
        <h3>Current Workflow</h3>
        <p>Nodes: {canvas.nodes.length}</p>
        <p>Connections: {canvas.connections.length}</p>
        {selectedNode && (
          <div className="selected-node-info">
            <h4>Selected Node</h4>
            <p>{getProduct(canvas.nodes.find(n => n.id === selectedNode)?.productId || '')?.name}</p>
          </div>
        )}
      </div>

      {/* CRUD Forms */}
      <ProductForm
        product={editingProduct}
        onSave={handleSaveProduct}
        onCancel={() => {
          setShowProductForm(false);
          setEditingProduct(undefined);
        }}
        isOpen={showProductForm}
      />
      
      <InterfaceForm
        interface={editingInterface}
        onSave={handleSaveInterface}
        onCancel={() => {
          setShowInterfaceForm(false);
          setEditingInterface(undefined);
        }}
        isOpen={showInterfaceForm}
      />
    </div>
  );
};

export default WorkflowBuilder;
